import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingIndicator from '../common/LoadingIndicator';
import FormAction from './FormAction';
import { LoadingStates as states } from '../constants/states';
import {
  buttonTypes,
  challengeFormats,
  challengeTypes,
  configurationKeys,
} from '../constants/clientConstants';
import { useTranslation } from 'react-i18next';
import PinInput from 'react-pin-input';
import ErrorBanner from '../common/ErrorBanner';
import langConfigService from '../services/langConfigService';
import redirectOnError from '../helpers/redirectOnError';
import ReCAPTCHA from 'react-google-recaptcha';

export default function OtpVerify({
  param,
  otpResponse,
  ID,
  authService,
  openIDConnectService,
  i18nKeyPrefix1 = 'otp',
  i18nKeyPrefix2 = 'errors',
}) {
  const { t: t1, i18n } = useTranslation('translation', {
    keyPrefix: i18nKeyPrefix1,
  });
  const { t: t2 } = useTranslation('translation', {
    keyPrefix: i18nKeyPrefix2,
  });

  const [langConfig, setLangConfig] = useState(null);

  useEffect(() => {
    async function loadLangConfig() {
      try {
        const config = await langConfigService.getEnLocaleConfiguration();
        setLangConfig(config);
      } catch (e) {
        console.error('Failed to load lang config', e);
        setLangConfig({ errors: { otp: {} } }); // Fallback to prevent crashes
      }
    }

    loadLangConfig();
  }, []);

  useEffect(() => {
    // SMS autofill and paste land the whole code in the first box. That box
    // only keeps one character unless we spread the digits ourselves.
    let filling = false;

    const otpRoot = () => document.getElementById('otp_verify_input');
    const otpInputs = (root) => Array.from(root.querySelectorAll('input'));

    const applyOtpAutocomplete = () => {
      const root = otpRoot();
      if (!root) return;
      otpInputs(root).forEach((input, index) => {
        input.setAttribute(
          'autocomplete',
          index === 0 ? 'one-time-code' : 'off'
        );
        input.setAttribute('inputmode', 'numeric');
        input.setAttribute(
          'name',
          index === 0 ? 'one-time-code' : `otp-digit-${index}`
        );
        if (index === 0) input.removeAttribute('maxlength');
      });
    };

    const fillOtpBoxes = (raw) => {
      const root = otpRoot();
      if (!root || filling) return;
      const inputs = otpInputs(root);
      const digits = String(raw || '')
        .replace(/\D/g, '')
        .slice(0, inputs.length);
      if (inputs.length < 2 || digits.length !== inputs.length) return;
      if (typeof DataTransfer !== 'function') return;
      filling = true;
      try {
        const data = new DataTransfer();
        data.setData('text', digits);
        const paste = new ClipboardEvent('paste', {
          bubbles: true,
          cancelable: true,
          clipboardData: data,
        });
        paste.__vfOtpFill = true;
        inputs[0].dispatchEvent(paste);
      } finally {
        filling = false;
      }
    };

    const onPaste = (event) => {
      if (event.__vfOtpFill) return;
      const root = otpRoot();
      if (!root || !root.contains(event.target)) return;
      const digits = (event.clipboardData?.getData('text') || '').replace(
        /\D/g,
        ''
      );
      if (digits.length < otpInputs(root).length) return;
      event.preventDefault();
      event.stopPropagation();
      fillOtpBoxes(digits);
    };

    const onInput = (event) => {
      const root = otpRoot();
      if (!root) return;
      const inputs = otpInputs(root);
      if (event.target !== inputs[0]) return;
      const digits = String(event.target.value || '').replace(/\D/g, '');
      if (digits.length < inputs.length) return;
      fillOtpBoxes(digits);
    };

    applyOtpAutocomplete();
    const observer = new MutationObserver(applyOtpAutocomplete);
    const container = otpRoot();
    if (container)
      observer.observe(container, { childList: true, subtree: true });
    document.addEventListener('paste', onPaste, true);
    document.addEventListener('input', onInput, true);
    return () => {
      observer.disconnect();
      document.removeEventListener('paste', onPaste, true);
      document.removeEventListener('input', onInput, true);
    };
  }, []);

  const fields = param;
  let fieldsState = {};
  fields.forEach((field) => (fieldsState['Otp' + field.id] = ''));

  const post_SendOtp = authService.post_SendOtp;
  const post_AuthenticateUser = authService.post_AuthenticateUser;
  const buildRedirectParams = authService.buildRedirectParamsV2;

  const resendOtpTimeout =
    openIDConnectService.getEsignetConfiguration(
      configurationKeys.resendOtpTimeout
    ) ?? process.env.REACT_APP_RESEND_OTP_TIMEOUT_IN_SEC;
  const commaSeparatedChannels =
    openIDConnectService.getEsignetConfiguration(
      configurationKeys.sendOtpChannels
    ) ?? process.env.REACT_APP_SEND_OTP_CHANNELS;
  const otpLengthValue =
    openIDConnectService.getEsignetConfiguration(configurationKeys.otpLength) ??
    process.env.REACT_APP_OTP_LENGTH;
  const otpLength = parseInt(otpLengthValue);

  const [status, setStatus] = useState({ state: states.LOADED, msg: '' });
  const [resendOtpCountDown, setResendOtpCountDown] = useState();
  const [showResendOtp, setShowResendOtp] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [timer, setTimer] = useState(null);
  const [otpValue, setOtpValue] = useState('');
  const [otpSentEmail, setOtpSentEmail] = useState('');
  const [otpSentMobile, setOtpSentMobile] = useState('');
  const [errorBanner, setErrorBanner] = useState(null);

  const captchaSiteKey =
    openIDConnectService.getEsignetConfiguration(
      configurationKeys.captchaSiteKey
    ) ?? process.env.REACT_APP_CAPTCHA_SITE_KEY;

  const captchaEnableComponents =
    openIDConnectService.getEsignetConfiguration(
      configurationKeys.captchaEnableComponents
    ) ?? process.env.REACT_APP_CAPTCHA_ENABLE;

  const captchaEnableComponentsList = captchaEnableComponents
    .split(',')
    .map((x) => x.trim().toLowerCase());

  const [showCaptcha, setShowCaptcha] = useState(
    captchaEnableComponentsList.indexOf('send-otp') !== -1
  );
  const [captchaToken, setCaptchaToken] = useState(null);
  const _reCaptchaRef = useRef(null);

  let pin = useRef();

  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    authenticateUser();
  };

  const handleSendOtp = (e) => {
    e.preventDefault();
    sendOTP();
  };

  /**
   * Reset the captcha widget
   * & its token value
   */
  const resetCaptcha = () => {
    _reCaptchaRef.current?.reset();
    setCaptchaToken(null);
  };

  const handleCaptchaChange = (value) => {
    setCaptchaToken(value);
  };

  const sendOTP = async () => {
    try {
      setErrorBanner(null);
      pin.clear();
      setOtpValue('');

      let transactionId = openIDConnectService.getTransactionId();
      let otpChannels = commaSeparatedChannels.split(',').map((x) => x.trim());

      let id = ID.prefix + ID.id + ID.postfix;
      let tempCaptchaToken = captchaToken;
      setCaptchaToken(null);

      setStatus({ state: states.LOADING, msg: 'sending_otp_msg' });
      const sendOtpResponse = await post_SendOtp(
        transactionId,
        id,
        otpChannels,
        tempCaptchaToken
      );
      setStatus({ state: states.LOADED, msg: '' });

      const { response, errors } = sendOtpResponse;

      if (errors !== null && errors.length > 0) {
        let errorCodeCondition =
          langConfig.errors.otp[errors[0].errorCode] !== undefined &&
          langConfig.errors.otp[errors[0].errorCode] !== null;

        if (errorCodeCondition) {
          setErrorBanner({
            errorCode: `otp.${errors[0].errorCode}`,
            show: true,
          });
        } else if (errors[0].errorCode === 'invalid_transaction') {
          redirectOnError(errors[0].errorCode, t2(`${errors[0].errorCode}`));
        } else {
          setErrorBanner({
            errorCode: `${errors[0].errorCode}`,
            show: true,
          });
        }
        return;
      } else {
        startTimer();
        setErrorBanner(null);
        setOtpSentMobile(response.maskedMobile);
        setOtpSentEmail(response.maskedEmail);
      }
    } catch (error) {
      setErrorBanner({
        errorCode: 'otp.send_otp_failed_msg',
        show: true,
      });
      setStatus({ state: states.ERROR, msg: '' });
    }
  };

  useEffect(() => {
    let loadComponent = async () => {
      i18n.on('languageChanged', function () {
        if (showCaptcha) {
          //to rerender recaptcha widget on language change
          setShowCaptcha(false);
          setTimeout(() => {
            setShowCaptcha(true);
          }, 1);
        }
      });
    };

    setShowTimer(false);
    setShowResendOtp(false);
    setErrorBanner(null);
    setOtpSentMobile(otpResponse.maskedMobile);
    setOtpSentEmail(otpResponse.maskedEmail);
    loadComponent();

    startTimer();
  }, []);

  const startTimer = async () => {
    clearInterval(timer);
    setResendOtpCountDown(
      t1('resend_otp_counter', getMinFromSec(resendOtpTimeout))
    );
    setShowResendOtp(false);
    setShowTimer(true);
    let timePassed = 0;
    var interval = setInterval(function () {
      timePassed++;
      let timeLeft = resendOtpTimeout - timePassed;
      setResendOtpCountDown(t1('resend_otp_counter', getMinFromSec(timeLeft)));

      if (timeLeft === 0) {
        clearInterval(interval);
        setShowTimer(false);
        setShowResendOtp(true);
        resetCaptcha();
      }
    }, 1000);
    setTimer(interval);
  };

  const getMinFromSec = (seconds) => {
    let sec = (seconds % 60).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    let min = Math.floor(seconds / 60).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    return { min: min, sec: sec };
  };

  const getOtpFieldLabel = (t1) => {
    const label = t1('otp_label_text');
    return label && label !== 'otp_label_text' ? label : 'OTP';
  };

  //Handle Login API Integration here
  const authenticateUser = async () => {
    try {
      let transactionId = openIDConnectService.getTransactionId();

      let challengeType = challengeTypes.otp;
      let challenge = otpValue;
      let challengeFormat = challengeFormats.otp;

      let challengeList = [
        {
          authFactorType: challengeType,
          challenge: challenge,
          format: challengeFormat,
        },
      ];

      let id = ID.prefix + ID.id + ID.postfix;

      setStatus({ state: states.LOADING, msg: 'authenticating_msg' });
      const authenticateResponse = await post_AuthenticateUser(
        transactionId,
        id,
        challengeList
      );
      setStatus({ state: states.LOADED, msg: '' });

      const { response, errors } = authenticateResponse;

      if (errors !== null && errors.length > 0) {
        let errorCodeCondition =
          langConfig.errors.otp[errors[0].errorCode] !== undefined &&
          langConfig.errors.otp[errors[0].errorCode] !== null;

        let fieldLabel = getOtpFieldLabel(t1);

        if (errorCodeCondition) {
          setErrorBanner({
            errorCode: `otp.${errors[0].errorCode}`,
            show: true,
            field: fieldLabel,
          });
        } else if (errors[0].errorCode === 'invalid_transaction') {
          redirectOnError(errors[0].errorCode, t2(`${errors[0].errorCode}`));
        } else {
          setErrorBanner({
            errorCode: `${errors[0].errorCode}`,
            show: true,
            field: fieldLabel,
          });
        }
        return;
      } else {
        setErrorBanner(null);
        let params = buildRedirectParams({
          nonce: openIDConnectService.getNonce(),
          state: openIDConnectService.getState(),
          oauthResponse: openIDConnectService.getOAuthDetails(),
          consentAction: response.consentAction || 'CAPTURE',
          ui_locales: i18n.language,
        });

        navigate(process.env.PUBLIC_URL + '/claim-details' + params, {
          replace: true,
        });
      }
    } catch (error) {
      setErrorBanner({
        errorCode: 'authentication_failed_msg',
        show: true,
      });
      setStatus({ state: states.ERROR, msg: '' });
    }
  };

  let styles = {
    width: '42px',
    height: '42px',
    margin: '0 8px',
    border: '',
    borderBottom: '2px solid #fff',
    color: '#fff',
  };

  if (window.screen.availWidth <= 375) {
    styles = { ...styles, width: '2.15em', margin: '0 6px' };
  }

  const fanNumber = `${ID?.prefix ?? ''}${ID?.id ?? ''}${ID?.postfix ?? ''}`;

  const onCloseHandle = () => {
    setErrorBanner(null);
  };

  return (
    <div className="text-white">
      {errorBanner !== null && (
        <ErrorBanner
          showBanner={errorBanner.show}
          errorCode={t2(errorBanner.errorCode, {
            field: errorBanner.field,
          })}
          onCloseHandle={onCloseHandle}
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <input
          type="text"
          value={fanNumber}
          readOnly
          disabled
          aria-label={t1('vid')}
          className="h-11 w-full rounded-lg border border-white/50 bg-transparent px-3 py-2.5 text-base text-white text-center tracking-wide placeholder:text-white/80 disabled:cursor-not-allowed shadow-none"
        />

        <div
          className="relative flex justify-center text-white pt-1 pb-1"
          id="otp_verify_input"
        >
          <PinInput
            length={otpLength}
            initialValue=""
            onChange={(value) => {
              setOtpValue(value);
            }}
            type="numeric"
            inputMode="number"
            style={{ padding: '8px 0px', color: '#fff' }}
            inputStyle={styles}
            inputFocusStyle={{ borderBottom: '2px solid #7dd3e8' }}
            onComplete={(value) => {
              setOtpValue(value);
            }}
            autoSelect={true}
            ref={(n) => (pin = n)}
            disabled={status.state === states.LOADING}
            focus={true}
          />
        </div>

        <div className="text-center break-words px-1">
          {status.state !== states.LOADING && !errorBanner && (
            <span className="w-full flex justify-center text-sm leading-relaxed text-white/90">
              {otpSentEmail && otpSentMobile
                ? t1('otp_sent_msg', {
                    otpChannels: t1('mobile_email_placeholder', {
                      mobileNumber: otpSentMobile,
                      emailAddress: otpSentEmail,
                    }),
                  })
                : otpSentEmail
                  ? t1('otp_sent_msg', {
                      otpChannels: t1('email_placeholder', {
                        emailAddress: otpSentEmail,
                      }),
                    })
                  : t1('otp_sent_msg', {
                      otpChannels: t1('mobile_placeholder', {
                        mobileNumber: otpSentMobile,
                      }),
                    })}
            </span>
          )}

          {status.state === states.LOADING && (
            <LoadingIndicator size="medium" message={status.msg} />
          )}
        </div>

        {showCaptcha && showResendOtp && (
          <div className="flex justify-center mt-5 mb-2">
            <ReCAPTCHA
              hl={i18n.language}
              ref={_reCaptchaRef}
              onChange={handleCaptchaChange}
              sitekey={captchaSiteKey}
            />
          </div>
        )}

        <FormAction
          disabled={
            otpValue.length !== otpLength || status.state === states.LOADING
          }
          type={buttonTypes.submit}
          text={t1('verify')}
          id="verify_otp"
        />

        {showTimer && (
          <span className="w-full flex justify-center text-sm text-white/90 mt-2">
            {resendOtpCountDown}
          </span>
        )}

        {showResendOtp && (
          <FormAction
            type={buttonTypes.button}
            text={t1('resend_otp')}
            handleClick={handleSendOtp}
            id="resend_otp"
            disabled={
              (showCaptcha && captchaToken === null) ||
              status.state === states.LOADING
            }
          />
        )}
      </form>
    </div>
  );
}
